'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  AeSupportedCity,
  BvPincodeValidationResult,
  BvResolvedUsPlanDisplay,
  buildUsBvCardResultFromShopifyPlansResponse,
  cityEqualsAeSupportedCity,
  cityMatchesAeSupportedCity,
  fetchAeSupportedCities,
  getBvStoreInputConfig,
  getUsBvSharedCardSnapshot,
  isUsBvShopifyPlansPayloadOk,
  isValidUsZip,
  normalizeAeCityForMatch,
  resolveBvStatusKind,
  sanitizeBvPrice,
  validateBvPincodeByStore,
  validateUsZipAndPricingWithMeta,
} from 'utils/bloodVisionUpsell';
import { BvUpsellCard } from 'content/products/bloodVisionUpsell';
import { useBvUsZipPlansSnapshot } from 'utils/useBvUsZipPlansSnapshot';
import { getCookie, setCookie } from 'utils/cookie';
import { bvUsZipPlansEnsureWarmFromCookie } from 'utils/bvUsZipPlansStore';

export type BvStatusKind =
  | 'info'
  | 'error'
  | 'warning'
  | 'success'
  | 'neutral';

interface UseBvPincodeValidatorArgs {
  card: BvUpsellCard;
  store: ShopifyStore | null;
  active: boolean;
  debounceMs?: number;
  autoFocusInput?: boolean;
}

interface CachedResult {
  isServiceable: boolean;
  message: string;
  resolvedPrice?: number;
  resolvedUsPlanDisplay?: BvResolvedUsPlanDisplay;
  resolvedShopifyVariantId?: string;
  hasResolvedBackendPrice?: boolean;
  reasonCode?: BvPincodeValidationResult['reasonCode'];
  plansRevision?: number;
}

const resetUsResolvedPricing = (
  setResolvedPrice: (v: number | undefined) => void,
  setHasResolvedBackendPrice: (v: boolean) => void,
  setResolvedUsPlanDisplay: (v: BvResolvedUsPlanDisplay | undefined) => void,
  setResolvedShopifyVariantId: (v: string | undefined) => void,
  fallbackPrice?: number,
) => {
  setResolvedPrice(sanitizeBvPrice(fallbackPrice));
  setHasResolvedBackendPrice(false);
  setResolvedUsPlanDisplay(undefined);
  setResolvedShopifyVariantId(undefined);
};

const cacheKeyForCardZip = (pin: string, id: string) => `${pin}|${id}`;
const validatorContextKey = (
  store: ShopifyStore | null,
  id: string,
  behavior: BvUpsellCard['pincodeBehavior'],
) => `${store ?? 'none'}|${id}|${behavior}`;
const requestKeyForCardZip = (
  store: ShopifyStore | null,
  id: string,
  pin: string,
  behavior: BvUpsellCard['pincodeBehavior'],
) => `${store ?? 'none'}|${id}|${behavior}|${pin}`;

const normalizeValidationInputForStore = (
  store: ShopifyStore | null,
  value: string,
) => {
  if (store === ShopifyStore.AE) return normalizeAeCityForMatch(value);
  return value.replace(/\D/g, '');
};

const applyUsValidationResultToState = (
  result: BvPincodeValidationResult,
  cardFallbackPrice: number | undefined,
  setters: {
    setIsServiceable: (v: boolean) => void;
    setStatusText: (v: string) => void;
    setStatusKind: (v: BvStatusKind) => void;
    setHasResolvedBackendPrice: (v: boolean) => void;
    setResolvedPrice: (v: number | undefined) => void;
    setResolvedUsPlanDisplay: (v: BvResolvedUsPlanDisplay | undefined) => void;
    setResolvedShopifyVariantId: (v: string | undefined) => void;
  },
) => {
  const {
    setIsServiceable,
    setStatusText,
    setStatusKind,
    setHasResolvedBackendPrice,
    setResolvedPrice,
    setResolvedUsPlanDisplay,
    setResolvedShopifyVariantId,
  } = setters;
  setIsServiceable(result.isServiceable);
  setStatusText(result.message);
  setStatusKind(resolveBvStatusKind(result));
  const resolvedFromBackend =
    typeof result.resolvedPrice === 'number' && !!result.resolvedUsPlanDisplay;
  setHasResolvedBackendPrice(resolvedFromBackend);
  if (typeof result.resolvedPrice === 'number') {
    setResolvedPrice(result.resolvedPrice);
  } else {
    setResolvedPrice(sanitizeBvPrice(cardFallbackPrice));
  }
  setResolvedUsPlanDisplay(result.resolvedUsPlanDisplay);
  setResolvedShopifyVariantId(result.resolvedShopifyVariantId);
};

const getBvPincodeCookieName = (store: ShopifyStore | null) =>
  store ? `bv_pincode_${store.toLowerCase()}` : null;

const readBvPincodeCookie = (store: ShopifyStore | null): string => {
  if (typeof document === 'undefined') return '';
  const cookieName = getBvPincodeCookieName(store);
  if (!cookieName) return '';
  const rawValue = getCookie(cookieName);
  if (typeof rawValue !== 'string') return '';
  if (store === ShopifyStore.AE) {
    try {
      return decodeURIComponent(rawValue).trim();
    } catch {
      return String(rawValue).trim();
    }
  }
  try {
    return decodeURIComponent(rawValue).replace(/\D/g, '');
  } catch {
    return String(rawValue).replace(/\D/g, '');
  }
};

const writeBvPincodeCookie = (store: ShopifyStore | null, pin: string) => {
  if (typeof document === 'undefined') return;
  const cookieName = getBvPincodeCookieName(store);
  if (!cookieName) return;
  if (store === ShopifyStore.AE) {
    const normalizedCity = pin.trim();
    if (!normalizedCity) return;
    setCookie(cookieName, normalizedCity, 30);
    return;
  }
  const normalized = pin.replace(/\D/g, '');
  if (!normalized) return;
  setCookie(cookieName, normalized, 30);
};

export const useBvPincodeValidator = ({
  card,
  store,
  active,
  debounceMs = 450,
  autoFocusInput = true,
}: UseBvPincodeValidatorArgs) => {
  type UsValidationMeta = Awaited<ReturnType<typeof validateUsZipAndPricingWithMeta>>;
  type StoreValidationResult = Awaited<ReturnType<typeof validateBvPincodeByStore>>;

  const isProduction =
    process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION';
  const inputConfig = useMemo(() => getBvStoreInputConfig(store), [store]);
  const zipPlans = useBvUsZipPlansSnapshot();

  const [pincode, setPincode] = useState('');
  const [statusText, setStatusText] = useState('');
  const [statusKind, setStatusKind] = useState<BvStatusKind>('neutral');
  const [isServiceable, setIsServiceable] = useState(false);
  const [isCheckingPincode, setIsCheckingPincode] = useState(false);
  const [resolvedPrice, setResolvedPrice] = useState<number | undefined>(
    sanitizeBvPrice(card.fallbackPrice),
  );
  const [hasResolvedBackendPrice, setHasResolvedBackendPrice] = useState(false);
  const [resolvedUsPlanDisplay, setResolvedUsPlanDisplay] = useState<
    BvResolvedUsPlanDisplay | undefined
  >(undefined);
  const [resolvedShopifyVariantId, setResolvedShopifyVariantId] = useState<
    string | undefined
  >(undefined);
  const [aeSupportedCities, setAeSupportedCities] = useState<AeSupportedCity[]>(
    [],
  );
  const [highlightedAeCityIndex, setHighlightedAeCityIndex] = useState(-1);
  const [isAeSuggestionsDismissed, setIsAeSuggestionsDismissed] =
    useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const cacheRef = useRef<Record<string, CachedResult>>({});
  const pendingUsValidationRef = useRef<Record<string, Promise<UsValidationMeta>>>(
    {},
  );
  const pendingStoreValidationRef = useRef<
    Record<string, Promise<StoreValidationResult>>
  >({});

  const hasAnyPincodeValue = pincode.length > 0;
  const isAeStore = store === ShopifyStore.AE;
  const normalizedValidationInput = useMemo(
    () => normalizeValidationInputForStore(store, pincode),
    [store, pincode],
  );
  const isPincodeFormatValid = inputConfig.pincodeRegex.test(pincode.trim());
  const shouldValidatePincode = isAeStore
    ? normalizedValidationInput.length >= 2
    : pincode.length === inputConfig.inputMaxLength && isPincodeFormatValid;
  const isUsValidation = card.pincodeBehavior === 'us_price_and_availability';
  const isInAeOptionalValidation = !isUsValidation;

  const cardId = card.id;
  const cardShopifyVariantId = card.shopifyVariantId;
  const cardPincodeBehavior = card.pincodeBehavior;
  const cardFallbackPrice = card.fallbackPrice;
  const cardUsPlanKey = card.usPlanKey;

  const usBvSharedCardSnapshot = useMemo(
    () =>
      getUsBvSharedCardSnapshot(
        card,
        zipPlans.committedZip,
        zipPlans.shopifyPlansResponse,
      ),
    [
      card,
      zipPlans.committedZip,
      zipPlans.revision,
      zipPlans.shopifyPlansResponse,
    ],
  );


  const prevSessionRef = useRef<{ active: boolean; contextKey: string }>({
    active: false,
    contextKey: '',
  });

  useEffect(() => {
    const contextKey = validatorContextKey(store, cardId, cardPincodeBehavior);
    if (!active) {
      prevSessionRef.current = { ...prevSessionRef.current, active: false };
      return;
    }
    const prev = prevSessionRef.current;
    const shouldReset = !prev.active || prev.contextKey !== contextKey;
    const contextChanged = prev.contextKey !== contextKey;
    prevSessionRef.current = { active: true, contextKey };
    if (!shouldReset) return;

    setStatusText('');
    setStatusKind('neutral');
    setIsCheckingPincode(false);
    setHasResolvedBackendPrice(false);
    setResolvedUsPlanDisplay(undefined);
    setResolvedShopifyVariantId(undefined);
    setResolvedPrice(sanitizeBvPrice(cardFallbackPrice));
    setHighlightedAeCityIndex(-1);
    setIsAeSuggestionsDismissed(false);
    requestIdRef.current += 1;
    if (contextChanged) {
      cacheRef.current = {};
      pendingUsValidationRef.current = {};
      pendingStoreValidationRef.current = {};
      setAeSupportedCities([]);
    }
    const cookieZip = readBvPincodeCookie(store);
    const prefill =
      isUsValidation &&
      store === ShopifyStore.US &&
      cookieZip &&
      zipPlans.committedZip === cookieZip &&
      zipPlans.shopifyPlansResponse &&
      isUsBvShopifyPlansPayloadOk(zipPlans.shopifyPlansResponse)
        ? {
            zip: zipPlans.committedZip,
            response: zipPlans.shopifyPlansResponse,
            revision: zipPlans.revision,
          }
        : null;

    if (prefill) {
      setPincode(prefill.zip);
      const result = buildUsBvCardResultFromShopifyPlansResponse(
        card,
        prefill.response,
      );
      applyUsValidationResultToState(result, cardFallbackPrice, {
        setIsServiceable,
        setStatusText,
        setStatusKind,
        setHasResolvedBackendPrice,
        setResolvedPrice,
        setResolvedUsPlanDisplay,
        setResolvedShopifyVariantId,
      });
      const resolvedFromBackend =
        typeof result.resolvedPrice === 'number' && !!result.resolvedUsPlanDisplay;
      cacheRef.current[
        cacheKeyForCardZip(prefill.zip, contextKey)
      ] = {
        isServiceable: result.isServiceable,
        message: result.message,
        resolvedPrice: result.resolvedPrice,
        resolvedUsPlanDisplay: result.resolvedUsPlanDisplay,
        resolvedShopifyVariantId: result.resolvedShopifyVariantId,
        hasResolvedBackendPrice: resolvedFromBackend,
        reasonCode: result.reasonCode,
        plansRevision: prefill.revision,
      };
    } else if (cookieZip) {
      setPincode(cookieZip);
    } else {
      setPincode('');
      setIsServiceable(!isProduction && !isUsValidation);
    }

    const animationId = autoFocusInput
      ? requestAnimationFrame(() => {
          inputRef.current?.focus({ preventScroll: true });
        })
      : null;
    return () => {
      if (animationId !== null) cancelAnimationFrame(animationId);
    };
  }, [
    active,
    isProduction,
    isUsValidation,
    cardFallbackPrice,
    cardId,
    store,
    zipPlans?.committedZip,
    zipPlans?.revision,
    zipPlans?.shopifyPlansResponse,
    cardShopifyVariantId,
    cardUsPlanKey,
    cardPincodeBehavior,
    autoFocusInput,
  ]);

  useEffect(() => {
    if (!active) return;
    if (
      store !== ShopifyStore.US ||
      card.pincodeBehavior !== 'us_price_and_availability'
    ) {
      return;
    }
    void bvUsZipPlansEnsureWarmFromCookie(card);
  }, [active, store, card]);

  useEffect(() => {
    if (!active || !isAeStore) return;
    if (aeSupportedCities.length > 0) return;

    let cancelled = false;
    void fetchAeSupportedCities()
      .then((cities) => {
        if (cancelled) return;
        setAeSupportedCities(cities);
      })
      .catch(() => {
        if (cancelled) return;
        setAeSupportedCities([]);
      });

    return () => {
      cancelled = true;
    };
  }, [active, isAeStore, aeSupportedCities.length]);

  const aeCitySuggestions = useMemo(() => {
    if (!isAeStore || normalizedValidationInput.length < 2) return [];
    return aeSupportedCities.filter((city) =>
      cityMatchesAeSupportedCity(pincode, city),
    );
  }, [isAeStore, normalizedValidationInput, aeSupportedCities, pincode]);

  const aeExactMatchedCity = useMemo(() => {
    if (!isAeStore || normalizedValidationInput.length < 2) return null;
    return (
      aeSupportedCities.find((city) => cityEqualsAeSupportedCity(pincode, city)) ??
      null
    );
  }, [isAeStore, normalizedValidationInput, aeSupportedCities, pincode]);

  const showAeCitySuggestions =
    isAeStore &&
    active &&
    normalizedValidationInput.length >= 2 &&
    aeCitySuggestions.length > 0 &&
    !isAeSuggestionsDismissed &&
    !aeExactMatchedCity &&
    !isServiceable;

  useEffect(() => {
    if (!active) return;

    if (!store) {
      setStatusText(
        'Store information is unavailable. Please refresh and try again.',
      );
      setStatusKind('error');
      setIsServiceable(false);
      setIsCheckingPincode(false);
      return;
    }

    const shouldBypassNonProdValidation =
      !isProduction && store === ShopifyStore.IN && isInAeOptionalValidation;

    if (shouldBypassNonProdValidation) {
      setStatusText(inputConfig.defaultAvailabilityHint);
      setStatusKind('info');
      setIsServiceable(true);
      setIsCheckingPincode(false);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (!hasAnyPincodeValue) {
      setStatusText(inputConfig.defaultAvailabilityHint);
      setStatusKind('info');
      setIsServiceable(false);
      setIsCheckingPincode(false);
      if (isUsValidation) {
        resetUsResolvedPricing(
          setResolvedPrice,
          setHasResolvedBackendPrice,
          setResolvedUsPlanDisplay,
          setResolvedShopifyVariantId,
          cardFallbackPrice,
        );
      }
      return;
    }

    if (!shouldValidatePincode) {
      setStatusText(inputConfig.invalidFormatMessage);
      setStatusKind('info');
      setIsServiceable(false);
      setIsCheckingPincode(false);
      if (isUsValidation) {
        resetUsResolvedPricing(
          setResolvedPrice,
          setHasResolvedBackendPrice,
          setResolvedUsPlanDisplay,
          setResolvedShopifyVariantId,
          cardFallbackPrice,
        );
      }
      return;
    }

    const contextKey = validatorContextKey(store, cardId, cardPincodeBehavior);
    const ck = cacheKeyForCardZip(normalizedValidationInput, contextKey);
    const cachedResult = cacheRef.current[ck];
    const revisionStale =
      isUsValidation &&
      !!zipPlans &&
      (cachedResult?.plansRevision ?? -1) !== zipPlans.revision;
    if (cachedResult && !revisionStale) {
      const resolvedStatusKind = resolveBvStatusKind({
        isServiceable: cachedResult.isServiceable,
        reasonCode: cachedResult.reasonCode,
      });
      setStatusText(cachedResult.message);
      setIsServiceable(cachedResult.isServiceable);
      setStatusKind(resolvedStatusKind);
      setHasResolvedBackendPrice(!!cachedResult.hasResolvedBackendPrice);
      if (typeof cachedResult.resolvedPrice === 'number') {
        setResolvedPrice(cachedResult.resolvedPrice);
      }
      setResolvedUsPlanDisplay(cachedResult.resolvedUsPlanDisplay);
      setResolvedShopifyVariantId(cachedResult.resolvedShopifyVariantId);
      if (isAeStore && cachedResult.isServiceable) {
        setIsAeSuggestionsDismissed(true);
        setHighlightedAeCityIndex(-1);
      }
      writeBvPincodeCookie(store, pincode);
      setIsCheckingPincode(false);
      return;
    }

    if (isUsValidation && zipPlans) {
      const reuse = zipPlans.getReuseForZip(pincode);
      if (reuse && isUsBvShopifyPlansPayloadOk(reuse.response)) {
        const result = buildUsBvCardResultFromShopifyPlansResponse(
          card,
          reuse.response,
        );
        applyUsValidationResultToState(result, cardFallbackPrice, {
          setIsServiceable,
          setStatusText,
          setStatusKind,
          setHasResolvedBackendPrice,
          setResolvedPrice,
          setResolvedUsPlanDisplay,
          setResolvedShopifyVariantId,
        });
        const resolvedFromBackend =
          typeof result.resolvedPrice === 'number' &&
          !!result.resolvedUsPlanDisplay;
        cacheRef.current[ck] = {
          isServiceable: result.isServiceable,
          message: result.message,
          resolvedPrice: result.resolvedPrice,
          resolvedUsPlanDisplay: result.resolvedUsPlanDisplay,
          resolvedShopifyVariantId: result.resolvedShopifyVariantId,
          hasResolvedBackendPrice: resolvedFromBackend,
          reasonCode: result.reasonCode,
          plansRevision: zipPlans.revision,
        };
        writeBvPincodeCookie(store, pincode);
        setIsCheckingPincode(false);
        return;
      }
    }

    setIsCheckingPincode(true);
    const currentRequestId = ++requestIdRef.current;
    const requestKey = requestKeyForCardZip(
      store,
      cardId,
      normalizedValidationInput,
      cardPincodeBehavior,
    );

    debounceTimerRef.current = setTimeout(async () => {
      if (isUsValidation && store === ShopifyStore.US) {
        const reuseBefore = zipPlans?.getReuseForZip(pincode) ?? null;
        let pending = pendingUsValidationRef.current[requestKey];
        if (!pending) {
          pending = validateUsZipAndPricingWithMeta(card, pincode, {
            reuseShopifyPlans: reuseBefore,
          });
          pendingUsValidationRef.current[requestKey] = pending;
          void pending.finally(() => {
            if (pendingUsValidationRef.current[requestKey] === pending) {
              delete pendingUsValidationRef.current[requestKey];
            }
          });
        }
        const meta = await pending;
        if (currentRequestId !== requestIdRef.current) return;

        applyUsValidationResultToState(meta.result, cardFallbackPrice, {
            setIsServiceable,
            setStatusText,
            setStatusKind,
            setHasResolvedBackendPrice,
            setResolvedPrice,
            setResolvedUsPlanDisplay,
            setResolvedShopifyVariantId,
          },
        );
        const resolvedFromBackend =
          typeof meta.result.resolvedPrice === 'number' &&
          !!meta.result.resolvedUsPlanDisplay;
        const freshUsPlansResponse =
          meta.shopifyPlansResponse &&
          isUsBvShopifyPlansPayloadOk(meta.shopifyPlansResponse)
            ? meta.shopifyPlansResponse
            : null;
        const didCommitFreshUsPlans =
          !!freshUsPlansResponse && !reuseBefore && !!zipPlans;
        cacheRef.current[ck] = {
          isServiceable: meta.result.isServiceable,
          message: meta.result.message,
          resolvedPrice: meta.result.resolvedPrice,
          resolvedUsPlanDisplay: meta.result.resolvedUsPlanDisplay,
          resolvedShopifyVariantId: meta.result.resolvedShopifyVariantId,
          hasResolvedBackendPrice: resolvedFromBackend,
          reasonCode: meta.result.reasonCode,
          plansRevision:
            typeof zipPlans?.revision === 'number'
              ? zipPlans.revision + (didCommitFreshUsPlans ? 1 : 0)
              : undefined,
        };
        if (didCommitFreshUsPlans) {
          zipPlans.commit(meta.normalizedZip, freshUsPlansResponse);
        }
        writeBvPincodeCookie(store, meta.normalizedZip);
        setIsCheckingPincode(false);
        return;
      }

      let pending = pendingStoreValidationRef.current[requestKey];
      if (!pending) {
        pending = validateBvPincodeByStore(store, card, pincode);
        pendingStoreValidationRef.current[requestKey] = pending;
        void pending.finally(() => {
          if (pendingStoreValidationRef.current[requestKey] === pending) {
            delete pendingStoreValidationRef.current[requestKey];
          }
        });
      }
      const result = await pending;
      if (currentRequestId !== requestIdRef.current) return;

      setIsServiceable(result.isServiceable);
      setStatusText(result.message);
      setStatusKind(resolveBvStatusKind(result));
      const resolvedFromBackend =
        typeof result.resolvedPrice === 'number' &&
        !!result.resolvedUsPlanDisplay;
      setHasResolvedBackendPrice(resolvedFromBackend);
      if (typeof result.resolvedPrice === 'number') {
        setResolvedPrice(result.resolvedPrice);
      } else {
        setResolvedPrice(sanitizeBvPrice(cardFallbackPrice));
      }
      setResolvedUsPlanDisplay(result.resolvedUsPlanDisplay);
      setResolvedShopifyVariantId(result.resolvedShopifyVariantId);
      if (isAeStore && result.isServiceable) {
        setIsAeSuggestionsDismissed(true);
        setHighlightedAeCityIndex(-1);
      }
      cacheRef.current[ck] = {
        isServiceable: result.isServiceable,
        message: result.message,
        resolvedPrice: result.resolvedPrice,
        resolvedUsPlanDisplay: result.resolvedUsPlanDisplay,
        resolvedShopifyVariantId: result.resolvedShopifyVariantId,
        hasResolvedBackendPrice: resolvedFromBackend,
        reasonCode: result.reasonCode,
      };
      writeBvPincodeCookie(store, pincode);
      setIsCheckingPincode(false);
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [
    active,
    isProduction,
    store,
    hasAnyPincodeValue,
    shouldValidatePincode,
    pincode,
    normalizedValidationInput,
    cardId,
    cardShopifyVariantId,
    cardPincodeBehavior,
    cardFallbackPrice,
    inputConfig.invalidFormatMessage,
    inputConfig.defaultAvailabilityHint,
    isInAeOptionalValidation,
    isUsValidation,
    debounceMs,
    cardUsPlanKey,
    zipPlans.revision,
  ]);

  const onPincodeChange = (value: string) => {
    const nextPincode =
      store === ShopifyStore.AE
        ? value.slice(0, inputConfig.inputMaxLength)
        : value.replace(/\D/g, '');
    if (nextPincode === pincode) return;
    setPincode(nextPincode);
    setHighlightedAeCityIndex(-1);
    setIsAeSuggestionsDismissed(false);
    setStatusText('');
    setStatusKind('neutral');
    setIsServiceable(false);
    setHasResolvedBackendPrice(false);
    if (isUsValidation) {
      resetUsResolvedPricing(
        setResolvedPrice,
        setHasResolvedBackendPrice,
        setResolvedUsPlanDisplay,
        setResolvedShopifyVariantId,
        cardFallbackPrice,
      );
      if (
        store === ShopifyStore.US &&
        (nextPincode.length === 0 || !isValidUsZip(nextPincode))
      ) {
        zipPlans?.clear();
      }
    }
    requestIdRef.current += 1;
    setIsCheckingPincode(false);
  };

  const onAeCitySuggestionSelect = (cityName: string) => {
    setPincode(cityName);
    setStatusText(`${cityName} is serviceable.`);
    setStatusKind('success');
    setIsServiceable(true);
    setIsCheckingPincode(false);
    setHighlightedAeCityIndex(-1);
    setIsAeSuggestionsDismissed(true);
    writeBvPincodeCookie(store, cityName);
  };

  const onAeCityInputFocus = () => {
    if (!isAeStore) return;
    setIsAeSuggestionsDismissed(false);
  };

  const closeAeCitySuggestions = () => {
    setIsAeSuggestionsDismissed(true);
    setHighlightedAeCityIndex(-1);
  };

  const setStatusMessage = (message: string, kind: BvStatusKind) => {
    setStatusText(message);
    setStatusKind(kind);
  };

  const canAddToCart = isInAeOptionalValidation ? true : isServiceable;

  return {
    pincode,
    onPincodeChange,
    inputRef,
    inputConfig,
    statusText,
    statusKind,
    isServiceable,
    canAddToCart,
    isCheckingPincode,
    resolvedPrice,
    hasResolvedBackendPrice,
    resolvedUsPlanDisplay,
    resolvedShopifyVariantId,
    setStatusMessage,
    usBvSharedCardSnapshot,
    usCommittedZip: zipPlans.committedZip,
    isAeCityMode: isAeStore,
    aeCitySuggestions,
    showAeCitySuggestions,
    highlightedAeCityIndex,
    setHighlightedAeCityIndex,
    onAeCitySuggestionSelect,
    onAeCityInputFocus,
    closeAeCitySuggestions,
  };
};
